import { useEffect, useState } from "react";

/**
 * Tailwind's `lg`. Exported so callers can reference the same threshold the
 * layout uses instead of hard-coding 1024 in a second place.
 */
export const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

/**
 * Reactive `matchMedia`.
 *
 * The initial value is read in a lazy state initialiser, not in an effect, so
 * the first render already knows the answer. Without that, a component that
 * renders a different element per branch flickers through the wrong branch on
 * mount — visible as a control appearing and vanishing.
 */
export function useMediaMatches(query: string): boolean {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/** True at `lg` and above, where the dashboard sidebar becomes a real sidebar. */
export function useIsDesktop(): boolean {
  return useMediaMatches(DESKTOP_MEDIA_QUERY);
}

export type VisualViewportState = {
  /**
   * Pixels of the screen the on-screen keyboard is covering — `0` when it is
   * closed, or when the browser does not report a separate visual viewport.
   *
   * Use this for information only. It is derived from `window.innerHeight`, and
   * Chrome does NOT keep that in step with its own browser bars, so on a phone it
   * can overstate the keyboard by the height of the address bar. `offsetTop` and
   * `height` below are the reliable pair — they describe the visible box itself.
   */
  inset: number;
  /** How far the visual viewport is scrolled inside the layout viewport. */
  offsetTop: number;
  /** Same, horizontally. */
  offsetLeft: number;
  /** Width of the visible area. */
  width: number;
  /**
   * Height of the area actually visible. Equals `window.innerHeight` when there is
   * no visual viewport to read, and shrinks with the keyboard when there is.
   */
  height: number;
};

function readViewport(): VisualViewportState {
  if (typeof window === "undefined") return { inset: 0, offsetTop: 0, offsetLeft: 0, width: 0, height: 0 };
  const vv = window.visualViewport;
  if (!vv) return { inset: 0, offsetTop: 0, offsetLeft: 0, width: window.innerWidth, height: window.innerHeight };
  return {
    inset: Math.max(0, Math.round(window.innerHeight - vv.height)),
    offsetTop: Math.max(0, Math.round(vv.offsetTop)),
    offsetLeft: Math.max(0, Math.round(vv.offsetLeft)),
    width: Math.round(vv.width),
    height: Math.round(vv.height),
  };
}

/**
 * The visible area, live.
 *
 * This is needed because the **layout** viewport does not shrink when the keyboard
 * opens: Android Chrome defaults to `interactive-widget=resizes-visual`, so `vh` and
 * `dvh` keep reporting the full height. Anything pinned to the bottom of a
 * `dvh`-sized box (a chat composer, for instance) therefore ends up underneath the
 * keyboard, and anything capped in `dvh` keeps its full height and gets squeezed
 * instead. `window.visualViewport` is the only thing that reports what is really on
 * screen while the keyboard is up.
 *
 * On a browser where `window.innerHeight` already tracks the keyboard, `inset` comes
 * out as 0 and callers fall back to their CSS sizing — no worse than not having this
 * at all.
 */
export function useKeyboardState(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(readViewport);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;

    // The keyboard animates in, so this fires many times per open. Coalesce to
    // one state update per frame to avoid a re-render storm while it slides up.
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setState((prev) => {
          const next = readViewport();
          // Handing back the SAME object when nothing moved is what keeps the
          // keyboard's stream of resize/scroll events from re-rendering the tree.
          return next.inset === prev.inset &&
            next.offsetTop === prev.offsetTop &&
            next.offsetLeft === prev.offsetLeft &&
            next.width === prev.width &&
            next.height === prev.height
            ? prev
            : next;
        });
      });
    };

    measure();
    vv.addEventListener("resize", measure);
    vv.addEventListener("scroll", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", measure);
      vv.removeEventListener("scroll", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  return state;
}

/** Just the keyboard height, for callers that do not need the rest. */
export function useKeyboardInset(): number {
  return useKeyboardState().inset;
}
